import { useEffect, useMemo, useState } from 'react';
import { ActionWorkspace } from './ActionWorkspace';
import { BuilderActionList } from './BuilderActionList';
import { CloseWorkspace } from './CloseWorkspace';
import { ReportWorkspace } from './ReportWorkspace';
import { loadActionForIssue } from '../services/actions';
import { createEvidencePreviewUrl, loadIssueRegister } from '../services/issues';
import type { ActionRecord } from '../types/actions';
import type { IssuePriority, IssueWithEvidence } from '../types/issues';

interface IssueRegisterProps {
  ownerId: string;
  assessmentId: string;
  onBack: () => void;
}

type Filter = 'all' | IssuePriority;
type RegisterMode = 'findings' | 'action' | 'actions' | 'close' | 'report';

export function IssueRegister({ ownerId, assessmentId, onBack }: IssueRegisterProps) {
  const [issues, setIssues] = useState<IssueWithEvidence[]>([]);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [filter, setFilter] = useState<Filter>('all');
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<RegisterMode>('findings');
  const [selectedIssue, setSelectedIssue] = useState<IssueWithEvidence | null>(null);
  const [selectedAction, setSelectedAction] = useState<ActionRecord | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const nextIssues = await loadIssueRegister(ownerId, assessmentId);
        if (!cancelled) {
          setIssues(nextIssues);
          setStatus('ready');
        }
      } catch (caught) {
        if (!cancelled) {
          setError(caught instanceof Error ? caught.message : 'Could not load findings.');
          setStatus('error');
        }
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [assessmentId, ownerId, refreshKey]);

  const visibleIssues = useMemo(
    () => issues.filter((issue) => filter === 'all' || issue.priority === filter),
    [filter, issues],
  );

  const urgentCount = issues.filter(
    (issue) => issue.priority === 'immediate' || issue.priority === 'urgent',
  ).length;
  const openCount = issues.filter((issue) => issue.status === 'open').length;

  function returnToFindings() {
    setMode('findings');
    setSelectedIssue(null);
    setSelectedAction(null);
    setRefreshKey((value) => value + 1);
  }

  function openActionForIssue(issueId: string) {
    const issue = issues.find((candidate) => candidate.id === issueId);
    if (!issue) {
      setError('Could not open this action because its source finding is no longer available.');
      return;
    }
    setSelectedIssue(issue);
    setMode('action');
  }

  async function openCloseForIssue(issue: IssueWithEvidence) {
    setError(null);
    try {
      const action = await loadActionForIssue(ownerId, issue.id);
      if (!action) {
        setError('Create an action before using the Close workflow.');
        return;
      }
      setSelectedIssue(issue);
      setSelectedAction(action);
      setMode('close');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not open Close for this finding.');
    }
  }

  if (mode === 'action' && selectedIssue) {
    return (
      <ActionWorkspace
        ownerId={ownerId}
        issue={selectedIssue}
        onBack={returnToFindings}
      />
    );
  }

  if (mode === 'close' && selectedIssue && selectedAction) {
    return (
      <CloseWorkspace
        ownerId={ownerId}
        issue={selectedIssue}
        action={selectedAction}
        onBack={returnToFindings}
        onActionUpdated={setSelectedAction}
      />
    );
  }

  if (mode === 'actions') {
    return (
      <BuilderActionList
        ownerId={ownerId}
        assessmentId={assessmentId}
        onBack={returnToFindings}
        onOpenAction={openActionForIssue}
      />
    );
  }

  if (mode === 'report') {
    return (
      <ReportWorkspace
        ownerId={ownerId}
        assessmentId={assessmentId}
        onBack={returnToFindings}
      />
    );
  }

  async function openEvidence(storagePath: string) {
    try {
      const url = await createEvidencePreviewUrl(storagePath);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not open the private photo.');
    }
  }

  return (
    <main className="page issue-register-page">
      <button className="text-button" type="button" onClick={onBack}>
        ← Back to inspection
      </button>

      <section className="issue-register-header">
        <span className="eyebrow">Issue register</span>
        <h1>Findings captured during this inspection</h1>
        <p className="lead">
          Keep observed concerns in one place, then convert the ones that need follow-up into accountable actions.
        </p>

        <div className="issue-stat-grid">
          <div><strong>{issues.length}</strong><span>Total findings</span></div>
          <div><strong>{openCount}</strong><span>Open</span></div>
          <div><strong>{urgentCount}</strong><span>Urgent</span></div>
          <div><strong>{issues.reduce((sum, issue) => sum + issue.evidence.length, 0)}</strong><span>Photos</span></div>
        </div>

        <div className="hero-actions issue-register-actions">
          <button className="button button-primary" type="button" onClick={() => setMode('actions')}>
            Open builder action list
          </button>
          <button className="button button-secondary ink-button" type="button" onClick={() => setMode('report')}>
            Open inspection report
          </button>
        </div>
      </section>

      {error ? <div className="status-banner" role="alert">{error}</div> : null}
      {status === 'loading' ? <div className="status-banner">Loading findings…</div> : null}

      <div className="issue-filters" aria-label="Finding priority filters">
        {(['all', 'immediate', 'urgent', 'important', 'monitor', 'minor'] as Filter[]).map((value) => (
          <button
            key={value}
            type="button"
            className={`filter-chip ${filter === value ? 'filter-chip-active' : ''}`}
            aria-pressed={filter === value}
            onClick={() => setFilter(value)}
          >
            {value === 'all' ? 'All' : value}
          </button>
        ))}
      </div>

      {status === 'ready' && visibleIssues.length === 0 ? (
        <section className="empty-findings">
          <strong>No findings in this view.</strong>
          <p>Issues marked during guided checks will appear here.</p>
        </section>
      ) : null}

      <section className="issue-list">
        {visibleIssues.map((issue) => {
          const closeAvailable = ['rectification_claimed', 'closed', 'waived'].includes(issue.status);
          return (
            <article className="issue-row" key={issue.id}>
              <div className="issue-row-topline">
                <span className={`priority-chip priority-${issue.priority}`}>{issue.priority}</span>
                <span>{issue.status.replaceAll('_', ' ')}</span>
              </div>
              <h2>{issue.title}</h2>
              <p>{issue.description || 'No additional note recorded.'}</p>
              <div className="issue-meta">
                <span>{issue.location}</span>
                <span>{issue.evidence.length} photo{issue.evidence.length === 1 ? '' : 's'}</span>
              </div>
              <div className="issue-action-row">
                <button
                  className="button button-secondary ink-button"
                  type="button"
                  onClick={() => {
                    setSelectedIssue(issue);
                    setMode('action');
                  }}
                >
                  {issue.status === 'open' ? 'Create action' : 'Manage action'}
                </button>
                {closeAvailable ? (
                  <button
                    className="button button-primary"
                    type="button"
                    onClick={() => void openCloseForIssue(issue)}
                  >
                    {issue.status === 'rectification_claimed' ? 'Recheck / close' : 'View closure'}
                  </button>
                ) : null}
                {issue.evidence.length > 0 ? (
                  <div className="evidence-links">
                    {issue.evidence.map((item, index) => (
                      <button
                        className="text-button"
                        type="button"
                        key={item.id}
                        onClick={() => void openEvidence(item.storage_path)}
                      >
                        View photo {index + 1}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            </article>
          );
        })}
      </section>
    </main>
  );
}
